import { useTranslation } from 'react-i18next';

import Spinner from '@/components/ui/Spinner';
import StatusPill from '@/components/ui/StatusPill';
import type { CrewActivity, ProductionState } from '@/hooks/useProduction';
import { crewIcon, crewPhaseLabel } from '@/lib/labels';
import { cn } from '@/lib/utils';

/**
 * Who on the virtual crew is working right now, and which roles have already had a
 * turn. The chips are clickable so a producer can ask the Director about a specific
 * department without knowing the pipeline.
 */
export default function ActiveCrewPanel({
  crew,
  crewHistory,
  phase,
  telemetry,
  live,
  onAskAbout,
}: {
  crew: CrewActivity | null;
  crewHistory: CrewActivity[];
  phase: string;
  telemetry: ProductionState['telemetry'];
  live: boolean;
  onAskAbout?: (role: string) => void;
}) {
  const { t } = useTranslation(['production', 'common']);
  const uniqueRoles = [...new Set(crewHistory.map((c) => c.role))];
  const phaseLabel = crewPhaseLabel(t, phase);
  const active = telemetry?.active_workers ?? 0;
  const cap = telemetry?.max_parallel_workers ?? 0;

  return (
    <div className="space-y-3">
      {crew && (
        <div className="flex items-center justify-between gap-3 rounded-lg border border-border bg-elevated px-3.5 py-3">
          <div className="min-w-0">
            <div className="flex items-center gap-1.5 font-display text-[13px] font-semibold">
              <span>{crewIcon(crew.role)}</span>
              <span className="truncate">{t(`crew.role.${crew.role}`, { defaultValue: crew.role })}</span>
            </div>
            <div className="mt-1 flex items-center gap-1.5 text-[11px] text-faint">
              {live && <Spinner />}
              <span className="truncate">{t(`crew.task.${crew.task}`, { defaultValue: crew.task })}</span>
            </div>
          </div>
          {phaseLabel && <StatusPill tone={live ? 'running' : 'neutral'}>{phaseLabel}</StatusPill>}
        </div>
      )}

      {active > 0 && (
        <div className="text-[11px] text-faint">
          {cap ? t('crew.parallelCapped', { active, cap }) : t('crew.parallel', { active })}
        </div>
      )}

      {uniqueRoles.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {uniqueRoles.map((role) => (
            <button
              key={role}
              type="button"
              onClick={() => onAskAbout?.(role)}
              title={t('crew.askAbout', { role: t(`crew.role.${role}`, { defaultValue: role }) })}
              className={cn(
                'rounded-full border px-2.5 py-1 text-[10.5px] transition-colors',
                crew?.role === role
                  ? 'border-primary/30 bg-primary/10 font-medium text-primary'
                  : 'border-border bg-elevated text-muted-foreground hover:border-primary/40 hover:text-foreground',
              )}
            >
              {t(`crew.role.${role}`, { defaultValue: role })}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
